import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Loader2, Trash2, XCircle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { api } from "@/lib/api";
import type { Language } from "@/lib/translations";
import type { DeleteAccountCopy } from "./copy";

// Mirrors DeleteAccountResponseSchema in backend/src/types.ts
interface DeleteAccountResponse {
  deleted: true;
  warnings: string[];
}

interface ConfirmStepProps {
  copy: DeleteAccountCopy;
  language: Language;
  email: string;
  onDeleted: () => void;
}

export function ConfirmStep({ copy, language, email, onDeleted }: ConfirmStepProps) {
  const t = copy.confirm;
  const [acknowledged, setAcknowledged] = useState<boolean>(false);

  const deletion = useMutation({
    mutationFn: async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("No active session");
      return api.post<DeleteAccountResponse>(
        "/api/account/delete",
        { language },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: async () => {
      // The server-side account is gone; clear the local session
      await supabase.auth.signOut().catch(() => undefined);
      onDeleted();
    },
  });

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <CardTitle className="text-base font-medium">
            <span className="text-muted-foreground">{t.signedInAs}</span>{" "}
            <span className="font-semibold break-all">{email}</span>
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => supabase.auth.signOut()}
            disabled={deletion.isPending}
          >
            {t.signOut}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <section>
          <h2 className="font-semibold text-foreground mb-3 flex items-center gap-2">
            <Trash2 className="w-4 h-4 text-destructive" />
            {t.deletedTitle}
          </h2>
          <ul className="space-y-2 text-sm text-muted-foreground list-disc pl-5">
            {t.deletedItems.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="font-semibold text-foreground mb-3 flex items-center gap-2">
            <XCircle className="w-4 h-4" />
            {t.keptTitle}
          </h2>
          <ul className="space-y-2 text-sm text-muted-foreground list-disc pl-5">
            {t.keptItems.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </section>

        <Alert className="border-amber-500/50 bg-amber-500/10">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-sm">{t.subscriptionWarning}</AlertDescription>
        </Alert>

        <div className="flex items-start gap-3">
          <Checkbox
            id="ack"
            checked={acknowledged}
            onCheckedChange={(v) => setAcknowledged(v === true)}
            disabled={deletion.isPending}
            className="mt-0.5"
          />
          <Label htmlFor="ack" className="text-sm leading-snug cursor-pointer">
            {t.ack}
          </Label>
        </div>

        {deletion.isError ? (
          <p className="text-sm text-destructive">{t.deleteError}</p>
        ) : null}

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="destructive"
              className="w-full min-h-[44px]"
              disabled={!acknowledged || deletion.isPending}
            >
              {deletion.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t.deleting}
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t.deleteButton}
                </>
              )}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t.dialogTitle}</AlertDialogTitle>
              <AlertDialogDescription>{t.dialogDesc}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t.dialogCancel}</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => deletion.mutate()}
              >
                {t.dialogConfirm}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
